import React, { useEffect, useMemo, useState } from "react";
import "./SolutionDetailsDownloads.css";

import pdfIcon from "../../../../assets/images/allsolutions/downloads/pdf.png";
import downloadIcon from "../../../../assets/images/allsolutions/downloads/download.png";
import { useTranslation } from "react-i18next";
import { getDownloads } from "../../../../api/downloads";
import { resolveMediaUrl } from "../../../../utils/media";

/**
 * @param {{
 *   solution?: Record<string, unknown>,
 *   items?: { id?: number|string, name?: string, title?: string, label?: string, fileUrl?: string, href?: string }[],
 *   hideWhenEmpty?: boolean,
 *   downloadsTag?: string,
 *   downloadsTitle?: string,
 *   downloadsDescription?: string,
 *   downloadAllText?: string,
 * }} props
 */
const SolutionDetailsDownloads = ({
  solution,
  items: itemsProp,
  hideWhenEmpty,
  downloadsTag: downloadsTagProp,
  downloadsTitle: downloadsTitleProp,
  downloadsDescription: downloadsDescriptionProp,
  downloadAllText: downloadAllTextProp,
}) => {
  const useStaticItems = itemsProp !== undefined;

  const staticList = useMemo(() => {
    if (!useStaticItems) return [];
    return (Array.isArray(itemsProp) ? itemsProp : [])
      .map((it, idx) => ({
        id: it?.id ?? idx + 1,
        name: it?.name || it?.title || it?.label || "Document",
        fileUrl: resolveMediaUrl(it?.fileUrl || it?.href),
      }))
      .filter((it) => it.fileUrl);
  }, [itemsProp, useStaticItems]);

  const { i18n } = useTranslation();
  const [apiDownloads, setApiDownloads] = useState([]);

  useEffect(() => {
    if (useStaticItems) return;
    let isMounted = true;
    const load = async () => {
      try {
        const data = await getDownloads({
          language: String(i18n.language || "en").toLowerCase(),
        });
        if (!isMounted) return;
        const normalized = (Array.isArray(data) ? data : [])
          .map((it, idx) => ({
            id: it?.id || idx + 1,
            name: it?.name || it?.title || it?.label || "",
            fileUrl: resolveMediaUrl(it?.fileUrl),
          }))
          .filter((it) => it.name);
        setApiDownloads(normalized);
      } catch {
        if (isMounted) setApiDownloads([]);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [i18n.language, useStaticItems]);

  const list = useStaticItems ? staticList : apiDownloads;

  const downloadsTag = downloadsTagProp ?? solution?.downloadsTag ?? "DOWNLOADS";
  const downloadsTitle = downloadsTitleProp ?? solution?.downloadsTitle ?? "Available downloads";
  const downloadsDescription =
    downloadsDescriptionProp ??
    solution?.downloadsDescription ??
    "IRQGYP is a leading Iraqi company specialized in gypsum products and modern construction solutions.";
  const downloadAllLabel = downloadAllTextProp ?? solution?.downloadAllText ?? "Download all files";

  if (hideWhenEmpty && useStaticItems && list.length === 0) {
    return null;
  }

  const handleDownload = (item) => {
    if (!item?.fileUrl) return;
    window.open(item.fileUrl, "_blank", "noopener,noreferrer");
  };

  const handleDownloadAll = () => {
    list.forEach((item) => {
      if (item?.fileUrl) {
        window.open(item.fileUrl, "_blank", "noopener,noreferrer");
      }
    });
  };

  return (
    <section className="solutionDetailsDownloads">
      <div className="solutionDetailsDownloads-container">
        <div className="solutionDetailsDownloads-tag">{downloadsTag}</div>

        <h2 className="solutionDetailsDownloads-title">{downloadsTitle}</h2>

        <div className="solutionDetailsDownloads-head">
          <p className="solutionDetailsDownloads-desc">{downloadsDescription}</p>

          <button
            type="button"
            className="solutionDetailsDownloads-downloadAllBtn"
            aria-label="Download all files"
            onClick={handleDownloadAll}
            disabled={!list.length}
          >
            <span>{downloadAllLabel}</span>
            <img src={downloadIcon} alt="" aria-hidden />
          </button>
        </div>

        <div className="solutionDetailsDownloads-list">
          {!list.length ? (
            <div className="solutionDetailsDownloads-item">
              <div className="solutionDetailsDownloads-left">
                <img src={pdfIcon} alt="" aria-hidden />
                <span>No downloads found.</span>
              </div>
            </div>
          ) : null}
          {list.map((item) => (
            <div className="solutionDetailsDownloads-item" key={item.id}>
              <div className="solutionDetailsDownloads-left">
                <img src={pdfIcon} alt="" aria-hidden />
                <span>{item.name}</span>
              </div>

              <button
                type="button"
                className="solutionDetailsDownloads-downloadBtn"
                aria-label={`Download ${item.name}`}
                onClick={() => handleDownload(item)}
                disabled={!item?.fileUrl}
              >
                <img src={downloadIcon} alt="" aria-hidden />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SolutionDetailsDownloads;
